import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Calendar, Tag, Loader2, AlertCircle, Search, X } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';

const NoticiasList = () => {
  const [noticias, setNoticias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroAtivo, setFiltroAtivo] = useState('Todas');
  const [busca, setBusca] = useState('');

  useEffect(() => {
    const fetchNoticias = async () => {
      try {
        setLoading(true);
        setError(null);
        const q = query(collection(db, 'noticias'), orderBy('data', 'desc'));
        const snapshot = await getDocs(q);
        const lista = snapshot.docs.map((doc) => {
          const d = doc.data();
          const dataVal = d.data;
          const dataStr = dataVal && typeof dataVal.toDate === 'function' ? dataVal.toDate().toISOString() : dataVal || new Date().toISOString();
          return { id: doc.id, ...d, data: dataStr };
        });
        setNoticias(lista);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchNoticias();
  }, []);

  const noticiasFiltradas = noticias
    .filter(n => filtroAtivo === 'Todas' || n.categoria === filtroAtivo)
    .filter(n => busca === '' || n.titulo.toLowerCase().includes(busca.toLowerCase()));

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#B49163]" size={40} /></div>;
  if (error) return <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>;

  const categorias = ['Todas', ...new Set(noticias.map(n => n.categoria))];

  return (
    <section className="py-12 px-6 max-w-7xl mx-auto">
      <div className="relative mb-8 max-w-md">
        <Search className="absolute left-3 top-3 text-gray-400" size={20} />
        <input type="text" placeholder="Buscar..." value={busca} onChange={(e) => setBusca(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#B49163] outline-none" />
      </div>
      <div className="flex flex-wrap gap-2 mb-10">
        {categorias.map(cat => (
          <button key={cat} onClick={() => setFiltroAtivo(cat)} className={`px-4 py-1 rounded-full text-sm font-medium ${filtroAtivo === cat ? 'bg-[#B49163] text-white' : 'border border-[#B49163] text-[#B49163]'}`}>{cat}</button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {noticiasFiltradas.map(n => (
          <Link key={n.id} to={`/noticias/${n.id}`} className="group">
            <Card className="h-full hover:shadow-xl transition-shadow border-none shadow-md overflow-hidden">
              {n.imagem_capa ? (
                <img
                  src={n.imagem_capa}
                  alt={n.titulo}
                  className="w-full h-48 object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                  <Tag size={28} className="text-gray-300" />
                </div>
              )}
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4"><Tag size={14} className="text-[#B49163]" /><span className="text-xs font-bold text-[#B49163] uppercase">{n.categoria}</span></div>
                <h3 className="text-xl font-serif font-bold mb-3 group-hover:text-[#B49163] transition-colors">{n.titulo}</h3>
                
                <p className="text-gray-600 text-sm mb-4">
                  {(() => {
                    const textoBase = n.resumo || n.conteudo_html || n.conteudoHtml || n.conteudo_completo || n.texto || '';
                    if (!textoBase) return 'Clique para ler a matéria completa e conferir os detalhes desta notícia...';
                    
                    const textoLimpo = textoBase.replace(/<[^>]*>?/gm, '').replace(/&nbsp;|\u00A0|\n/g, ' ').trim();
                    return textoLimpo.length > 136 ? textoLimpo.substring(0, 136) + '...' : textoLimpo;
                  })()}
                </p>

                <div className="flex items-center gap-2 text-xs text-gray-400"><Calendar size={12} /><span>{new Date(n.data).toLocaleDateString('pt-BR')}</span></div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  );
};
export default NoticiasList;